/* eslint-disable @typescript-eslint/no-explicit-any */
import { createPopper } from '@popperjs/core';
import React, { ReactNode, useEffect } from 'react';
import { IPopoverGerenalPlacements, IPopoverOptions } from '../../interfaces';

import './EpPopover.scss';

interface EpPopoverProps {
  popoverId: string;
  popoverHostId: string;
  popoverContentType?:
    | 'text'
    | 'simple-html'
    | 'interactive-html'
    | 'custom-html';
  popoverEventType?: 'hover' | 'click';
  popoverPlacement?: 'auto' | IPopoverGerenalPlacements;
  popoverTitle?: string;
  popoverTitleHasHtml?: boolean;
  popoverText?: string;
  selectorsToDismissPopover?: string[];
  customClasses?: string;
  triggerPopover: ReactNode;
  popoverBodyContent?: ReactNode;
}

let popper: any;

const handleItemsToDismissPopover = (
  popoverElement: Element,
  selectorsToDismissPopover: string[],
): void => {
  selectorsToDismissPopover.forEach((selector: string) => {
    const elements: NodeListOf<Element> =
      popoverElement.querySelectorAll(selector);

    if (elements) {
      elements.forEach((element: Element): void => {
        element.addEventListener('click', (): void => {
          hidePopover(popoverElement);
        });
      });
    }
  });
};

const handleHoverEventsPopover = (
  popoverElement: Element,
  triggerElement: Element,
): void => {
  let shouldHidePopover: boolean;

  triggerElement.addEventListener('mouseleave', () => {
    shouldHidePopover = true;
  });
  popoverElement.addEventListener('mouseleave', () => {
    shouldHidePopover = true;
  });

  triggerElement.addEventListener('mouseover', () => {
    shouldHidePopover = false;
  });
  popoverElement.addEventListener('mouseover', () => {
    shouldHidePopover = false;
  });

  document.addEventListener('mousemove', () => {
    if (shouldHidePopover) hidePopover(popoverElement);
  });
};

const initializePopover = (
  triggerElement: Element | null,
  popoverId: string,
  selectorsToDismissPopover: string[],
): void => {
  const popoverElement: HTMLElement | null =
    document.querySelector(`#${popoverId}`) || null;

  if (!popoverElement || !triggerElement) return;

  const popoverPlacement: IPopoverGerenalPlacements =
    popoverElement.getAttribute('data-placement') as IPopoverGerenalPlacements;
  const popoverEventType: 'hover' | 'click' = popoverElement.getAttribute(
    'data-event-type',
  ) as 'hover' | 'click';
  const popoverContentType:
    | 'text'
    | 'simple-html'
    | 'interactive-html'
    | 'custom-html' = popoverElement.getAttribute('data-content-type') as
    | 'text'
    | 'simple-html'
    | 'interactive-html'
    | 'custom-html';
  const popoverOptions: IPopoverOptions = {};

  if (popoverPlacement) popoverOptions.placement = popoverPlacement;

  popper = {
    instance: createPopper(triggerElement, popoverElement, popoverOptions),

    toggleListenersState(isEnabled: boolean): void {
      this?.instance?.setOptions((options: any) => ({
        ...options,
        modifiers: [
          ...options.modifiers,
          {
            name: 'eventListeners',
            enabled: isEnabled,
          },
        ],
      }));
    },
  };

  if (popoverEventType === 'hover') {
    const hoverShowEvents: string[] = ['mouseenter', 'focus'];
    const hideEvents = ['mouseleave', 'blur'];

    hoverShowEvents.forEach((event) => {
      triggerElement.addEventListener(event, () => {
        showPopover(popoverElement);

        if (popoverContentType === 'interactive-html') {
          handleHoverEventsPopover(popoverElement, triggerElement);
          handleItemsToDismissPopover(
            popoverElement,
            selectorsToDismissPopover,
          );
        }
      });
    });

    if (popoverContentType !== 'interactive-html') {
      hideEvents.forEach((event) => {
        triggerElement.addEventListener(event, () => hidePopover(popoverElement),
        );
      });
    }
  } else {
    triggerElement.addEventListener('click', () => {
      togglePopoverVisibility(popoverElement);

      if (
        popoverContentType === 'interactive-html' &&
        selectorsToDismissPopover.length > 0
      ) {
        handleItemsToDismissPopover(popoverElement, selectorsToDismissPopover);
      }
    });
  }
};

const showPopover = (popoverElement: Element): void => {
  popoverElement.setAttribute('data-show', '');
  popper?.toggleListenersState(true);
  popper.instance.update();
};

const hidePopover = (popoverElement: Element): void => {
  popoverElement.removeAttribute('data-show');
  popper?.toggleListenersState(false);
};

const togglePopoverVisibility = (popoverElement: Element): void => {
  if (popoverElement.hasAttribute('data-show')) {
    hidePopover(popoverElement);
    return;
  }

  showPopover(popoverElement);
};

export const EpPopover: React.FC<EpPopoverProps> = ({
  popoverId,
  popoverHostId,
  popoverContentType = 'text',
  popoverEventType = 'hover',
  popoverPlacement = 'auto',
  popoverTitle,
  popoverTitleHasHtml = false,
  popoverText = '',
  customClasses,
  triggerPopover,
  selectorsToDismissPopover = [],
  popoverBodyContent,
}) => {
  useEffect(() => {
    const hostComponent = document.querySelector(`#${popoverHostId}`);
    const trigger = hostComponent?.querySelector('.popover__trigger') || null;

    initializePopover(
      trigger,
      popoverId,
      selectorsToDismissPopover && selectorsToDismissPopover,
    );

    return () => {
      if (popper) {
        popper.instance.destroy();
        popper = null;
      }
    };
  }, [popoverHostId, popoverId, selectorsToDismissPopover]);

  return (
    <div id={popoverHostId}>
      {triggerPopover}

      <div
        id={popoverId}
        role="tooltip"
        className={`
          ep-popover
          ep-popover--${popoverPlacement}
          ${
    popoverContentType === 'interactive-html' ||
            popoverContentType === 'custom-html'
      ? 'ep-popover--with-html'
      : ''
    }
          ${customClasses ? customClasses : ''}
        `}
        data-placement={popoverPlacement}
        data-content-type={popoverContentType}
        data-event-type={popoverEventType}
      >
        {popoverTitle && !popoverTitleHasHtml && (
          <h3 className="ep-popover__title">{popoverTitle}</h3>
        )}

        {popoverTitle && popoverTitleHasHtml && (
          <h3
            className="ep-popover__title"
            dangerouslySetInnerHTML={{ __html: popoverTitle }}
          />
        )}

        {popoverContentType === 'simple-html' && (
          <p
            className="ep-popover__text"
            dangerouslySetInnerHTML={{ __html: popoverText }}
          />
        )}

        {popoverContentType === 'text' && (
          <p className="ep-popover__text">{popoverText}</p>
        )}

        {popoverContentType === 'interactive-html' && popoverBodyContent}
        {popoverContentType === 'custom-html' && popoverBodyContent}
      </div>
    </div>
  );
};
